'use client'

import { type ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconSearch } from '@redteclab/icons'

import { Input } from 'base-ui'

import { useMarketplacePartnerRegisterPageAutocompleteKeyboardNavigation } from '../../../hooks/useMarketplacePartnerRegisterPageAutocompleteKeyboardNavigation'
import { useMarketplacePartnerRegisterPageAutocompleteState } from '../../../hooks/useMarketplacePartnerRegisterPageAutocompleteState'
import { useMarketplacePartnerRegisterPageBrandsSearchInfinite } from '../../../hooks/useMarketplacePartnerRegisterPageBrandsSearchInfinite'
import { useMarketplacePartnerRegisterPageInfiniteScrollHandler } from '../../../hooks/useMarketplacePartnerRegisterPageInfiniteScrollHandler'

import { MarketplacePartnerRegisterPageAutocompleteChip } from './MarketplacePartnerRegisterPageAutocompleteChip'
import { MarketplacePartnerRegisterPageAutocompleteOption } from './MarketplacePartnerRegisterPageAutocompleteOption'

export const MarketplacePartnerRegisterPageAutocomplete = (): ReactElement => {
  const intl = useIntl()

  const {
    handleInputChange,
    handleOptionClick,
    handleRemoveChip,
    highlightedIndex,
    inputValue,
    selectedBrands,
    selectedValues,
    setHighlightedIndex,
  } = useMarketplacePartnerRegisterPageAutocompleteState()

  const { hasMore, isLoading, loadMore, options } =
    useMarketplacePartnerRegisterPageBrandsSearchInfinite({
      query: inputValue,
    })

  const { handleKeyDown, optionRefs } =
    useMarketplacePartnerRegisterPageAutocompleteKeyboardNavigation({
      highlightedIndex,
      onOptionSelect: handleOptionClick,
      options,
      setHighlightedIndex,
    })
  const { handleScroll, scrollContainerRef } =
    useMarketplacePartnerRegisterPageInfiniteScrollHandler({
      hasMore,
      isLoading,
      onLoadMore: loadMore,
    })

  let content: ReactElement | null = null
  if (options.length > 0) {
    content = (
      <div
        className="mt-3 flex max-h-96 flex-col overflow-y-auto"
        onScroll={handleScroll}
        ref={scrollContainerRef}
      >
        <ul
          className="m-0 list-none p-0 outline-none"
          id="autocomplete-listbox"
        >
          {options.map((option, index) => (
            <MarketplacePartnerRegisterPageAutocompleteOption
              isHighlighted={index === highlightedIndex}
              isSelected={selectedValues.has(option.value)}
              key={option.value}
              onClick={() => {
                handleOptionClick(option)
              }}
              option={option}
              setRef={(el) => {
                if (el) {
                  optionRefs.current.set(option.value, el)
                } else {
                  optionRefs.current.delete(option.value)
                }
              }}
            />
          ))}
        </ul>
      </div>
    )
  } else if (isLoading) {
    content = (
      <div className="flex items-center justify-center py-3 text-dark-primary-strong">
        <FormattedMessage id="marketplacePartnerRegisterPage.brands.autocomplete.loading" />
      </div>
    )
  } else {
    content = (
      <div className="flex items-center justify-center py-3 text-dark-primary-strong">
        <FormattedMessage id="marketplacePartnerRegisterPage.brands.autocomplete.notFound" />
      </div>
    )
  }

  return (
    <div className="relative w-full">
      {selectedBrands.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {selectedBrands.map((option) => (
            <MarketplacePartnerRegisterPageAutocompleteChip
              key={option.value}
              label={option.label}
              onRemove={() => {
                handleRemoveChip(option.value)
              }}
            />
          ))}
        </div>
      )}
      <Input
        className="w-full"
        endAdornment={
          <IconSearch className="mr-2 size-6 shrink-0 text-dark-brand" />
        }
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={intl.formatMessage({
          defaultMessage: 'Suchen',
          id: 'marketplacePartnerRegisterPage.brands.autocomplete.placeholder',
        })}
        type="text"
        value={inputValue}
      />
      {content}
    </div>
  )
}
